import { createContext, useContext, useReducer, Dispatch, useMemo } from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { TUserDetailsSimplified } from '../../../../apis/users/users.interfaces';
import { TDocument } from '../../../../apis/documents/documents.interfaces';
import { useStaffDocumentsHook } from '../hooks/useStaffDocumentsHook';
import { useUserDetailsListHook } from '../../users/hooks/useUserDetailsListHook';

export enum ACTION_TYPE {
  SET_IS_MANAGE_DOCUMENT_OPEN = 'SET_IS_MANAGE_DOCUMENT_OPEN',
  SET_SELECTED_DOCUMENT = 'SET_SELECTED_DOCUMENT',
  SET_SELECTED_DOCUMENT_ID = 'SET_SELECTED_DOCUMENT_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_DOCUMENT_LOADING = 'SET_IS_DOCUMENT_LOADING',
  SET_IS_DOCUMENT_ERROR = 'SET_IS_DOCUMENT_ERROR',
  SET_DOCUMENT_ERROR_MESSAGE = 'SET_DOCUMENT_ERROR_MESSAGE',
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_STAFF_DOCUMENTS = 'UPDATE_STAFF_DOCUMENTS',
  UPDATE_DOCUMENTS_LIST = 'UPDATE_DOCUMENTS_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type TStaffDocumentsAppState = {
  isManageDocumentOpen: boolean;
  isDocumentLoading: boolean;
  isDocumentError: boolean;
  documentErrorMessage: string;
  selectedDocument: TDocument | null;
  selectedDocumentId: string | null;
  isCreateMode: boolean;
  isEditMode: boolean;
  documentsList: TDocument[];
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
};

type Action =
  | { type: ACTION_TYPE.SET_IS_MANAGE_DOCUMENT_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_DOCUMENT_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_DOCUMENT_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE; payload: string }
  | { type: ACTION_TYPE.SET_SELECTED_DOCUMENT; payload: TDocument | null }
  | { type: ACTION_TYPE.SET_SELECTED_DOCUMENT_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.UPDATE_STAFF_DOCUMENTS; payload: TDocument[] }
  | { type: ACTION_TYPE.UPDATE_DOCUMENTS_LIST; payload: TDocument[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialStaffDocumentsState: TStaffDocumentsAppState = {
  isManageDocumentOpen: false,
  isDocumentLoading: false,
  isDocumentError: false,
  documentErrorMessage: '',
  selectedDocument: null,
  selectedDocumentId: null,
  isCreateMode: false,
  isEditMode: false,
  documentsList: [],
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
};

type TStaffDocumentsState = {
  staffDocumentsAppState: TStaffDocumentsAppState;
  staffDocumentRecords: TDocument[];
  simplifiedStaffObject: Record<string, TUserDetailsSimplified>;
};

const StaffDocumentsContext = createContext<TStaffDocumentsState | undefined>(
  undefined
);

const StaffDocumentsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const staffDocumentsReducer = (
  state: TStaffDocumentsAppState,
  action: Action
): TStaffDocumentsAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        userParams: { ...state.userParams, ...action.payload },
      };
    case ACTION_TYPE.SET_IS_MANAGE_DOCUMENT_OPEN:
      return { ...state, isManageDocumentOpen: action.payload };
    case ACTION_TYPE.SET_IS_DOCUMENT_LOADING:
      return { ...state, isDocumentLoading: action.payload };
    case ACTION_TYPE.SET_IS_DOCUMENT_ERROR:
      return { ...state, isDocumentError: action.payload };
    case ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE:
      return { ...state, documentErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_DOCUMENT:
      return { ...state, selectedDocument: action.payload };
    case ACTION_TYPE.SET_SELECTED_DOCUMENT_ID:
      return { ...state, selectedDocumentId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };
    case ACTION_TYPE.UPDATE_STAFF_DOCUMENTS:
      return { ...state, documentsList: action.payload };
    case ACTION_TYPE.UPDATE_DOCUMENTS_LIST:
      return { ...state, documentsList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    default:
      return state;
  }
};

export const StaffDocumentsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: staffDocumentsData = [] } = useStaffDocumentsHook({
    page: 1,
    limit: 100,
  });
  const { data: userDetailsListData } = useUserDetailsListHook({
    page: 1,
    limit: 100,
  });

  const [state, dispatch] = useReducer(staffDocumentsReducer, {
    ...initialStaffDocumentsState,
    documentsList: staffDocumentsData,
  });

  const simplifiedStaffObject = useMemo<Record<string, TUserDetailsSimplified>>(
    () => {
      if (!userDetailsListData?.data) {
        return {};
      }
      return userDetailsListData.data.reduce(
        (acc, staff) => {
          acc[staff.id] = {
            id: staff.id,
            basicDetails: {
              firstName: staff.basicDetails.firstName,
              middleName: staff.basicDetails.middleName,
              lastName: staff.basicDetails.lastName,
              gender: staff.basicDetails.gender,
            },
          };

          return acc;
        },
        {} as Record<string, TUserDetailsSimplified>
      );
    },
    [userDetailsListData]
  );

  const contextValue: TStaffDocumentsState = {
    staffDocumentsAppState: state,
    staffDocumentRecords: state.documentsList,
    simplifiedStaffObject,
  };

  return (
    <StaffDocumentsContext.Provider value={contextValue}>
      <StaffDocumentsDispatchContext.Provider value={dispatch}>
        {children}
      </StaffDocumentsDispatchContext.Provider>
    </StaffDocumentsContext.Provider>
  );
};

export const useStaffDocumentsState = () => {
  const context = useContext(StaffDocumentsContext);
  if (context === undefined) {
    throw new Error(
      'useStaffDocumentsState must be used within a StaffDocumentsProvider'
    );
  }
  return context;
};

export const useStaffDocumentsDispatch = () => {
  const context = useContext(StaffDocumentsDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useStaffDocumentsDispatch must be used within a StaffDocumentsProvider'
    );
  }
  return context;
};

export const useStaffDocumentsActions = () => {
  const dispatch = useStaffDocumentsDispatch();
  if (dispatch === undefined) {
    throw new Error(
      'useStaffDocumentsActions must be used within a StaffDocumentsProvider'
    );
  }

  const setIsManageDocumentOpen = (isOpen: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MANAGE_DOCUMENT_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedDocument = (document: TDocument | null) => {
    dispatch?.({ type: ACTION_TYPE.SET_SELECTED_DOCUMENT, payload: document });
  };

  const setIsDocumentLoading = (isLoading: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_DOCUMENT_LOADING,
      payload: isLoading,
    });
  };

  const setIsDocumentError = (isError: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_DOCUMENT_ERROR, payload: isError });
  };

  const setDocumentErrorMessage = (errorMessage: string) => {
    dispatch?.({
      type: ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedDocumentId = (documentId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_DOCUMENT_ID,
      payload: documentId,
    });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  return {
    setIsManageDocumentOpen,
    setSelectedDocument,
    setIsDocumentLoading,
    setIsDocumentError,
    setDocumentErrorMessage,
    setSelectedDocumentId,
    setIsCreateMode,
    setIsEditMode,
  };
};
